import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, CheckCircle2, Clock, MapPin, KeyRound, ChevronRight, AlertCircle, Sparkles } from "lucide-react";
import api from "@/lib/api";
import KPICard from "@/components/KPICard";
import StateBadge from "@/components/StateBadge";
import EmptyState from "@/components/EmptyState";
import { TASK_STATE_LABELS, TASK_TYPE_LABELS } from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

function fmtH(min) {
    if (!min) return "—";
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (m === 0) return `${h}h`;
    if (h === 0) return `${m}min`;
    return `${h}h ${m}min`;
}

export default function OperatorDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        api.get("/operator/dashboard")
            .then((r) => { if (active) setData(r.data); })
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, []);

    if (loading || !data) return <div className="zentra-skeleton h-96" data-testid="op-dashboard-loading" />;
    const k = data.kpis;
    const todayLabel = new Date(data.today_iso).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

    return (
        <div className="space-y-6" data-testid="op-dashboard">
            <div>
                <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                    {todayLabel}
                </p>
                <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                    Hola, {user?.name?.split(" ")[0] || "Operario"}
                </h2>
                <p className="text-sm text-zentra-muted mt-1">
                    Tus tareas asignadas para hoy y esta semana.
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPICard title="Hoy" value={k.tasks_today} suffix="tareas" icon={ClipboardList} accent testId="op-kpi-today" />
                <KPICard title="Completadas" value={k.tasks_completed_today} suffix={`/${k.tasks_today}`} icon={CheckCircle2} testId="op-kpi-done" />
                <KPICard title="Tiempo estimado hoy" value={fmtH(k.minutes_today)} suffix="" icon={Clock} testId="op-kpi-time" />
                <KPICard title="Esta semana" value={k.tasks_week} suffix={fmtH(k.minutes_week)} icon={Sparkles} testId="op-kpi-week" />
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-semibold text-zentra-ink">Tareas de hoy</h3>
                    <Link to="/operario/tareas" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                        Ver todas →
                    </Link>
                </div>
                {data.today_tasks.length === 0 ? (
                    <EmptyState icon={ClipboardList} title="Sin tareas hoy" description="Disfruta el día libre." />
                ) : (
                    <div className="space-y-2" data-testid="op-today-list">
                        {data.today_tasks.map((t) => (
                            <Link
                                key={t.id}
                                to={`/operario/tareas/${t.id}`}
                                className="zentra-card p-4 flex items-center gap-3"
                                data-testid={`op-today-${t.id}`}
                            >
                                <div className="w-14 text-center">
                                    <p className="font-mono-zentra text-zentra-muted text-[10px] uppercase">hora</p>
                                    <p className="font-display text-sm font-bold text-zentra-ink">
                                        {t.start_time || "—"}
                                    </p>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-display font-semibold text-zentra-ink truncate">{t.property_name || "Propiedad"}</p>
                                        {t.service_type_text && (
                                            <span className="zentra-chip bg-zentra-accent/15 text-zentra-primary border-zentra-accent text-[10px]">
                                                {t.service_type_text}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-zentra-muted mt-0.5">
                                        <span className="font-mono-zentra">{t.property_code}</span>
                                        <span>· {TASK_TYPE_LABELS[t.type] || t.type}</span>
                                        <span>· {fmtH(t.estimated_minutes)}</span>
                                        {t.key_access && <span className="inline-flex items-center gap-0.5"><KeyRound size={10} /> {t.key_access}</span>}
                                    </div>
                                </div>
                                <StateBadge value={t.state} dict={TASK_STATE_LABELS} />
                                <ChevronRight size={16} className="text-zentra-muted" />
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
